import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FaSpinner, FaBolt, FaExclamationTriangle, FaCreditCard } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Amounts arrive as exact decimal strings. They are formatted for display only
// and never parsed into a float for arithmetic -- the server owns every sum.
const money = (value, currency = 'usd') => {
  if (value === null || value === undefined || value === '') return '--';
  const symbol = currency === 'usd' ? '$' : '';
  const asNumber = Number(value);
  if (Number.isNaN(asNumber)) return `${symbol}${value}`;
  return `${symbol}${asNumber.toFixed(2)}`;
};

const perCredit = (value, currency = 'usd') => {
  if (value === null || value === undefined || value === '') return '--';
  const symbol = currency === 'usd' ? '$' : '';
  const asNumber = Number(value);
  if (Number.isNaN(asNumber)) return `${symbol}${value}`;
  // Sub-cent rates are real ($0.005/credit), so show enough places to be honest.
  const places = asNumber < 0.01 ? 4 : 2;
  return `${symbol}${asNumber.toFixed(places)} / credit`;
};

/**
 * On-Demand Metered Usage.
 *
 * Everything shown here comes from the server: status, price, cap bounds and
 * spend. The component never computes a charge, never sends a price, and treats
 * its own optimism as untrustworthy -- after enabling it re-reads the
 * authoritative summary rather than assuming the transition succeeded.
 */
const MeteredUsagePanel = ({ onChanged }) => {
  const { token } = useAuth();
  const { isDarkMode } = useTheme();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [available, setAvailable] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [showEnable, setShowEnable] = useState(false);
  const [capInput, setCapInput] = useState('');
  const [consent, setConsent] = useState(false);
  const [editingCap, setEditingCap] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/credits/metered/summary`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 404) {
        // Feature flag off for this deployment: show nothing at all.
        setAvailable(false);
        return;
      }
      if (!response.ok) throw new Error('Could not load usage settings.');
      const data = await response.json();
      setSummary(data);
      setCapInput((current) => current || data.metered_monthly_cap
        || data.cap_default || '');
      setError('');
    } catch (err) {
      setError(err.message || 'Could not load usage settings.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const post = async (path, body, method = 'POST') => {
    const response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: { 'Content-Type': 'application/json',
                 Authorization: `Bearer ${token}` },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const err = new Error(data.error || 'Request failed.');
      err.code = data.code;
      throw err;
    }
    return data;
  };

  const startCardSetup = async () => {
    setBusy('setup');
    setError('');
    try {
      const data = await post('/api/credits/metered/payment-method/setup', {
        return_url: `${window.location.origin}/dashboard`,
      });
      if (data.setup_url) {
        // Stripe-hosted: card details never touch this app.
        window.location.href = data.setup_url;
        return;
      }
      setError('Card setup is unavailable right now.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const enable = async () => {
    setBusy('enable');
    setError('');
    try {
      await post('/api/credits/metered/enable', {
        monthly_cap: String(capInput).trim(),
        accept_automatic_charges: consent,
      });
      setShowEnable(false);
      setConsent(false);
      // Re-read rather than trusting the response: only the backend decides
      // whether provisioning actually reached ACTIVE.
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      if (err.code === 'metered_payment_method_required') {
        await startCardSetup();
        return;
      }
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const disable = async () => {
    setBusy('disable');
    setError('');
    try {
      await post('/api/credits/metered/disable');
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const saveCap = async () => {
    setBusy('cap');
    setError('');
    try {
      await post('/api/credits/metered/cap',
                 { monthly_cap: String(capInput).trim() }, 'PUT');
      setEditingCap(false);
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  if (!available) return null;

  const cardClass = `p-4 rounded-xl border ${isDarkMode
    ? 'bg-gray-800/40 border-gray-700/40' : 'bg-white/60 border-gray-200/60'}`;
  const mutedClass = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const headingClass = isDarkMode ? 'text-white' : 'text-gray-900';

  if (loading) {
    return (
      <div className={cardClass}>
        <FaSpinner className="animate-spin w-5 h-5 text-brand-red" />
      </div>
    );
  }

  const status = summary?.metered_status || 'DISABLED';
  const currency = summary?.metered_currency || 'usd';
  const isActive = status === 'ACTIVE';
  const paymentRequired = status === 'PAYMENT_REQUIRED';
  const suspended = status === 'SUSPENDED';
  const provisioning = status === 'PROVISIONING';
  const configured = summary?.policy_configured;

  const row = (label, value) => (
    <div className="flex justify-between text-sm py-0.5">
      <span className={mutedClass}>{label}</span>
      <span className={headingClass}>{value}</span>
    </div>
  );

  const capEditor = (
    <div className="flex items-center gap-2 mt-2">
      <input
        type="text"
        inputMode="decimal"
        value={capInput}
        onChange={(e) => setCapInput(e.target.value)}
        aria-label="Monthly spending limit"
        className={`w-28 px-2 py-1 rounded border text-sm ${isDarkMode
          ? 'bg-gray-900/60 border-gray-600 text-white'
          : 'bg-white border-gray-300 text-gray-900'}`}
      />
      <span className={`text-xs ${mutedClass}`}>
        {summary?.cap_minimum && summary?.cap_maximum
          ? `${money(summary.cap_minimum, currency)} - ${money(summary.cap_maximum, currency)}`
          : ''}
      </span>
    </div>
  );

  return (
    <div className={`${cardClass} space-y-3`}>
      <div className="flex items-center justify-between">
        <h3 className={`font-semibold flex items-center ${headingClass}`}>
          <FaBolt className="w-4 h-4 mr-2 text-brand-red" />
          On-Demand Metered Usage
        </h3>
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          isActive ? 'bg-green-500/20 text-green-600 dark:text-green-400'
            : paymentRequired ? 'bg-yellow-500/20 text-yellow-700 dark:text-yellow-400'
            : 'bg-gray-500/20 ' + mutedClass}`}>
          {isActive ? 'ON' : status.replace(/_/g, ' ').toLowerCase()}
        </span>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/30 text-red-600 dark:text-red-400 px-3 py-2 rounded text-sm">
          {error}
        </div>
      )}

      {!configured && (
        <p className={`text-sm ${mutedClass}`}>
          On-Demand Usage is not available yet. An administrator must save the
          collection threshold, cap policy, and per-credit price in Credit
          Management first.
        </p>
      )}

      {/* Payment needs attention. Free and prepaid credits are unaffected, and
          saying so plainly is the difference between a scary message and an
          actionable one. */}
      {paymentRequired && (
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded p-3 space-y-2">
          <p className={`text-sm font-medium flex items-center ${headingClass}`}>
            <FaExclamationTriangle className="w-4 h-4 mr-2 text-yellow-600" />
            On-demand usage paused
          </p>
          <p className={`text-sm ${mutedClass}`}>
            We couldn&apos;t collect a metered usage payment. Your free and
            prepaid credits can still be used.
          </p>
          <div className="flex gap-2">
            <button onClick={startCardSetup} disabled={busy === 'setup'}
              className="px-3 py-1.5 text-sm font-medium text-white bg-brand-red rounded hover:bg-brand-red-dark disabled:opacity-50">
              {busy === 'setup' ? 'Opening...' : 'Update Payment Method'}
            </button>
          </div>
        </div>
      )}

      {suspended && (
        <p className={`text-sm ${mutedClass}`}>
          On-demand usage is suspended for this account. Please contact support.
        </p>
      )}

      {provisioning && (
        <p className={`text-sm ${mutedClass}`}>
          Setting up on-demand billing...
        </p>
      )}

      {/* Active view */}
      {isActive && (
        <div className="space-y-1">
          {row('Current rate', perCredit(summary.metered_unit_price, currency))}
          {row('Usage this cycle', money(summary.metered_period_spend, currency))}
          {row('Monthly hard cap', money(summary.metered_monthly_cap, currency))}
          {row('Remaining cap', money(summary.metered_cap_remaining, currency))}
          {row('Payment method', summary.payment_method_last4
            ? `${(summary.payment_method_brand || 'card').toUpperCase()} ****${summary.payment_method_last4}`
            : 'On file')}
          {editingCap ? (
            <>
              {capEditor}
              <div className="flex gap-2 mt-2">
                <button onClick={saveCap} disabled={busy === 'cap'}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-brand-red rounded hover:bg-brand-red-dark disabled:opacity-50">
                  {busy === 'cap' ? 'Saving...' : 'Save Limit'}
                </button>
                <button onClick={() => setEditingCap(false)}
                  className={`px-3 py-1.5 text-sm rounded border ${isDarkMode
                    ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-700'}`}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <div className="flex flex-wrap gap-2 pt-2">
              <button onClick={() => setEditingCap(true)}
                className={`px-3 py-1.5 text-sm rounded border ${isDarkMode
                  ? 'border-gray-600 text-white hover:bg-gray-800'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                Change Limit
              </button>
              <button onClick={startCardSetup} disabled={busy === 'setup'}
                className={`px-3 py-1.5 text-sm rounded border disabled:opacity-50 ${isDarkMode
                  ? 'border-gray-600 text-white hover:bg-gray-800'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}>
                Update Payment Method
              </button>
              <button onClick={disable} disabled={busy === 'disable'}
                className="px-3 py-1.5 text-sm rounded border border-red-500/40 text-red-600 dark:text-red-400 hover:bg-red-500/10 disabled:opacity-50">
                {busy === 'disable' ? 'Turning off...' : 'Turn Off'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Off view: the offer, the price, and the consent */}
      {!isActive && !suspended && !provisioning && configured && (
        <div className="space-y-2">
          <p className={`text-sm ${mutedClass}`}>
            Keep working after your free and prepaid credits are exhausted.
          </p>
          {row('Current price', perCredit(summary.metered_unit_price, currency))}
          <p className={`text-xs ${mutedClass}`}>
            Charges may be collected when usage reaches the billing threshold and
            at the end of your billing period.
          </p>

          {!showEnable ? (
            <button onClick={() => setShowEnable(true)}
              className="mt-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark rounded-lg hover:from-brand-red-dark">
              Enable On-Demand Usage
            </button>
          ) : (
            <div className="space-y-2 pt-2">
              <label className={`block text-sm font-medium ${headingClass}`}>
                Monthly spending limit
              </label>
              {capEditor}
              <label className={`flex items-start gap-2 text-xs ${mutedClass}`}>
                <input type="checkbox" checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  className="mt-0.5" />
                <span>
                  By enabling On-Demand Metered Usage, you authorize ZapRF to
                  charge your saved payment method for metered usage at the
                  displayed per-credit rate. Charges may occur when the billing
                  threshold is reached and at the end of your billing period.
                </span>
              </label>
              <div className="flex gap-2">
                <button onClick={enable}
                  disabled={!consent || !String(capInput).trim() || busy === 'enable'}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-brand-red to-brand-red-dark rounded-lg disabled:opacity-50">
                  {busy === 'enable' ? (
                    <><FaSpinner className="animate-spin w-3 h-3 inline mr-2" />Enabling...</>
                  ) : summary.payment_method_ready ? 'Confirm & Enable' : (
                    <><FaCreditCard className="w-3 h-3 inline mr-2" />Add Card & Enable</>
                  )}
                </button>
                <button onClick={() => { setShowEnable(false); setConsent(false); }}
                  className={`px-3 py-2 text-sm rounded border ${isDarkMode
                    ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-700'}`}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MeteredUsagePanel;
